import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailCustomerCards, type CustomerCard } from '@/api/retailCustomerCards';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.customer_card.manage';

const STATUS_LABEL: Record<string, string> = {
  active: 'Активна',
  blocked: 'Блокована',
  lost: 'Загублена',
  expired: 'Прострочена',
};

export default function CustomerCardsPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-customer-cards', orgId, statusFilter],
    queryFn: () =>
      retailCustomerCards.list({
        organization_id: orgId || undefined,
        status: statusFilter ? (statusFilter as 'active' | 'blocked' | 'lost' | 'expired') : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<CustomerCard>[]>(
    () => [
      {
        key: 'card_code',
        header: 'Код картки',
        width: 160,
        sortValue: (r) => r.card_code ?? '',
        cell: (r) => r.card_code,
      },
      {
        key: 'holder_name',
        header: 'Власник',
        sortValue: (r) => r.holder_name ?? '',
        cell: (r) => r.holder_name,
      },
      {
        key: 'holder_phone',
        header: 'Телефон',
        width: 160,
        sortValue: (r) => r.holder_phone ?? '',
        cell: (r) => r.holder_phone,
      },
      {
        key: 'bonus_balance',
        header: 'Бонусний баланс',
        width: 170,
        align: 'right',
        sortValue: (r) => Number(r.bonus_balance),
        cell: (r) =>
          r.bonus_balance
            ? Number(r.bonus_balance).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '0,00',
      },
      {
        key: 'status',
        header: 'Статус',
        width: 130,
        sortValue: (r) => r.status ?? '',
        cell: (r) => STATUS_LABEL[r.status] ?? r.status,
      },
      {
        key: 'issued_at',
        header: 'Видана',
        width: 150,
        sortValue: (r) => r.issued_at ?? '',
        cell: (r) => (r.issued_at ? new Date(r.issued_at).toLocaleDateString('uk-UA') : ''),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав керувати картками клієнтів (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Картки клієнтів
          <HintBadge
            title="Що це?"
            text={
              'Картка лояльності — це ідентифікатор постійного клієнта з бонусним балансом.\n\n' +
              'Що зберігаємо:\n' +
              '• Код картки — те, що касир вводить на касі або сканує (зазвичай номер телефону).\n' +
              '• ПIБ, телефон, email — для зв\'язку та програм лояльності.\n' +
              '• Бонусний баланс — поточна кількість бонусів.\n' +
              '• Дата народження — для бонусів на ДН.\n\n' +
              'Як працює на POS:\n' +
              '• Касир сканує/вводить код картки → бачить ПIБ + баланс.\n' +
              '• При продажу: можна списати бонуси (до макс. % з «Налаштувань лояльності»).\n' +
              '• Після продажу: нараховуються нові бонуси.\n\n' +
              'Баланс не редагується вручну — тільки через «Бонусні транзакції» або автоматично з чеків.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі статуси</option>
          <option value="active">Активна</option>
          <option value="blocked">Блокована</option>
          <option value="lost">Загублена</option>
          <option value="expired">Прострочена</option>
        </select>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canCreate}
          title={tooltipNew}
          onClick={() => navigate('/retail/customer-cards/new')}
        >
          + Нова картка
        </button>
      </div>
      <DataTable<CustomerCard>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає карток."
        onRowDoubleClick={(r) => navigate(`/retail/customer-cards/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="card_code"
      />
    </>
  );
}
